import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Branches() {
  const { refreshBranches } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState({ name: "", address: "", phone: "", gstin: "" });

  const load = () => api.get("/branches").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    setF(edit ? { name: edit.name, address: edit.address || "", phone: edit.phone || "", gstin: edit.gstin || "" }
      : { name: "", address: "", phone: "", gstin: "" });
  }, [edit, open]);

  const save = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    try {
      if (edit) await api.patch(`/branches/${edit.id}`, f);
      else await api.post("/branches", f);
      toast.success("Saved");
      setOpen(false); setEdit(null);
      await load(); await refreshBranches();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this branch? Existing data stays linked.")) return;
    try { await api.delete(`/branches/${id}`); await load(); await refreshBranches(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="branches-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Organization</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Branches</h1>
        </div>
        <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-branch-btn"><Plus className="w-4 h-4 mr-1" />New branch</Button>
      </div>
      <Card className="p-4">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Address</TableHead><TableHead>Phone</TableHead><TableHead>GSTIN</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map(b => (
              <TableRow key={b.id} data-testid={`branch-row-${b.id}`}>
                <TableCell>
                  <span className="font-medium">{b.name}</span>
                  {b.is_default && <Badge variant="secondary" className="ml-2 text-[10px]">DEFAULT</Badge>}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{b.address || "—"}</TableCell>
                <TableCell className="font-mono text-sm">{b.phone || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{b.gstin || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => { setEdit(b); setOpen(true); }}>Edit</Button>
                  {!b.is_default && <Button variant="ghost" size="sm" onClick={() => del(b.id)}><Trash2 className="w-4 h-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No branches.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEdit(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{edit ? "Edit" : "New"} branch</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div><Label>Name *</Label><Input value={f.name} onChange={e => setF(p => ({ ...p, name: e.target.value }))} data-testid="branch-name" /></div>
            <div><Label>Address</Label><Input value={f.address} onChange={e => setF(p => ({ ...p, address: e.target.value }))} /></div>
            <div><Label>Phone</Label><Input value={f.phone} onChange={e => setF(p => ({ ...p, phone: e.target.value }))} /></div>
            <div><Label>GSTIN</Label><Input value={f.gstin} onChange={e => setF(p => ({ ...p, gstin: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-branch">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
